import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Button, ButtonGroup, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight, UnfoldLess, UnfoldMore, FileDownload } from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { BASE_URL } from '../config';

// ── Tipi ───────────────────────────────────────────────────────────────────

interface ExportData {
  struttura: string | null;
  cognome: string;
  nome: string;
  id_hrp: number | null;
  progetto_titolo: string;
  pm: string;
  cliente: string;
  budget_gg_uu: number;
  budget_residuo_gg_uu: number;
  mese: number;
  percentuale: number;
}

interface ProgettoRow {
  id_hrp: number | null;
  progetto_titolo: string;
  pm: string;
  cliente: string;
  budget_gg_uu: number;
  budget_residuo_gg_uu: number;
  mesi: Record<number, number>;
  percAll: number;
}

interface RisorsaGroup {
  key: string;
  struttura: string;
  cognome: string;
  nome: string;
  progetti: ProgettoRow[];
  mesiTotali: Record<number, number>;
  percAll: number;
}

// ── Costanti ────────────────────────────────────────────────────────────────

const MESI_LABELS: Record<number, string> = {
  1: 'GEN', 2: 'FEB', 3: 'MAR', 4: 'APR', 5: 'MAG', 6: 'GIU',
  7: 'LUG', 8: 'AGO', 9: 'SET', 10: 'OTT', 11: 'NOV', 12: 'DIC',
};

const HEADER_BG   = 'FF1976D2';
const HEADER_FG   = 'FFFFFFFF';
const ROW_ODD_BG  = 'FFFFFFFF';
const ROW_EVEN_BG = 'FFF0F4FA';
const BORDER_COLOR = 'FFB0BEC5';

const thinBorder: Partial<ExcelJS.Borders> = {
  top:    { style: 'thin', color: { argb: BORDER_COLOR } },
  left:   { style: 'thin', color: { argb: BORDER_COLOR } },
  bottom: { style: 'thin', color: { argb: BORDER_COLOR } },
  right:  { style: 'thin', color: { argb: BORDER_COLOR } },
};

// ── Helper: colore % All ────────────────────────────────────────────────────

function percAllStyle(perc: number) {
  if (perc >= 95) return { backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 };
  if (perc >= 70) return { backgroundColor: '#fff8e1', color: '#e65100', fontWeight: 700 };
  return { backgroundColor: '#ffebee', color: '#c62828', fontWeight: 700 };
}

// ── Helper: costruzione gruppi ──────────────────────────────────────────────

function buildGroups(data: ExportData[], mesi: number[]): RisorsaGroup[] {
  const map = new Map<string, RisorsaGroup>();

  data.forEach(row => {
    const risorsaKey = `${row.struttura || ''}|${row.cognome}|${row.nome}`;

    if (!map.has(risorsaKey)) {
      const mesiTotali: Record<number, number> = {};
      mesi.forEach(m => { mesiTotali[m] = 0; });
      map.set(risorsaKey, {
        key: risorsaKey,
        struttura: row.struttura || '',
        cognome: row.cognome,
        nome: row.nome,
        progetti: [],
        mesiTotali,
        percAll: 0,
      });
    }

    const risorsa = map.get(risorsaKey)!;

    const progKey = `${row.id_hrp ?? ''}|${row.progetto_titolo}`;
    let prog = risorsa.progetti.find(
      p => `${p.id_hrp ?? ''}|${p.progetto_titolo}` === progKey
    );
    if (!prog) {
      const progMesi: Record<number, number> = {};
      mesi.forEach(m => { progMesi[m] = 0; });
      prog = {
        id_hrp: row.id_hrp,
        progetto_titolo: row.progetto_titolo,
        pm: row.pm || '',
        cliente: row.cliente || '',
        budget_gg_uu: row.budget_gg_uu || 0,
        budget_residuo_gg_uu: row.budget_residuo_gg_uu || 0,
        mesi: progMesi,
        percAll: 0,
      };
      risorsa.progetti.push(prog);
    }

    prog.mesi[row.mese] = (prog.mesi[row.mese] || 0) + row.percentuale;
    risorsa.mesiTotali[row.mese] = (risorsa.mesiTotali[row.mese] || 0) + row.percentuale;
  });

  const n = mesi.length || 1;
  map.forEach(risorsa => {
    const totMesi = Object.values(risorsa.mesiTotali).reduce((s, v) => s + v, 0);
    risorsa.percAll = Math.round(totMesi / n);
    risorsa.progetti.forEach(p => {
      const totProg = Object.values(p.mesi).reduce((s, v) => s + v, 0);
      p.percAll = Math.round(totProg / n);
    });
  });

  return Array.from(map.values()).sort((a, b) => {
    const s = a.struttura.localeCompare(b.struttura, 'it');
    return s !== 0 ? s : a.cognome.localeCompare(b.cognome, 'it');
  });
}

// ── Stili header ────────────────────────────────────────────────────────────

const thSx = {
  backgroundColor: '#1976d2',
  color: '#fff',
  fontWeight: 700,
  fontSize: '0.75rem',
  padding: '6px 8px',
  whiteSpace: 'nowrap',
  borderRight: '1px solid rgba(255,255,255,0.2)',
};

const thMeseSx = { ...thSx, textAlign: 'center' as const, width: 42, minWidth: 42 };
const thPercAllSx = { ...thSx, textAlign: 'center' as const, width: 60, minWidth: 60 };

const CURRENT_YEAR = new Date().getFullYear();
const ANNO_OPTIONS = Array.from({ length: CURRENT_YEAR - 2020 + 2 }, (_, i) => 2020 + i);

// ── Componente principale ───────────────────────────────────────────────────

const PianificazioneVistaPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<RisorsaGroup[]>([]);
  const [mesi, setMesi] = useState<number[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [strutturaFilter, setStrutturaFilter] = useState<string>('');
  const [anno, setAnno] = useState<number>(CURRENT_YEAR);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/export/pianificazione/${anno}`);
      if (!res.ok) throw new Error('Errore nel recupero dei dati');
      const json = await res.json();
      setMesi(json.mesi);
      setGroups(buildGroups(json.data, json.mesi));
      setExpanded(new Set());
      setStrutturaFilter('');
    } catch (e: any) {
      setError(e.message || 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [anno]);

  useEffect(() => { loadData(); }, [loadData]);

  const strutture = useMemo(() => {
    const set = new Set(groups.map(g => g.struttura).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'it'));
  }, [groups]);

  const visibleGroups = useMemo(() =>
    strutturaFilter ? groups.filter(g => g.struttura === strutturaFilter) : groups,
    [groups, strutturaFilter]
  );

  const toggleExpand = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const expandAll = () => setExpanded(new Set(visibleGroups.map(g => g.key)));
  const collapseAll = () => setExpanded(new Set());

  // ── Export Excel (dai dati già in memoria, rispetta filtro struttura) ─────

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(`Pianificazione ${anno}`);

      const staticColumns: Partial<ExcelJS.Column>[] = [
        { header: 'Struttura',              key: 'Struttura',              width: 22 },
        { header: 'Cognome',                key: 'Cognome',                width: 16 },
        { header: 'Nome',                   key: 'Nome',                   width: 16 },
        { header: 'ID HRP',                 key: 'ID HRP',                 width: 10 },
        { header: 'Titolo Progetto',        key: 'Titolo Progetto',        width: 34 },
        { header: 'PM',                     key: 'PM',                     width: 22 },
        { header: 'Cliente',                key: 'Cliente',                width: 26 },
        { header: 'Budget (gg/uu)',         key: 'Budget (gg/uu)',         width: 16 },
        { header: 'Budget Residuo (gg/uu)', key: 'Budget Residuo (gg/uu)', width: 20 },
      ];
      const monthColumns: Partial<ExcelJS.Column>[] = mesi.map(m => ({
        header: MESI_LABELS[m], key: MESI_LABELS[m], width: 8,
      }));
      worksheet.columns = [...staticColumns, ...monthColumns];

      // Righe: una per ogni risorsa-progetto nei gruppi visibili
      visibleGroups.forEach(risorsa => {
        risorsa.progetti.forEach(prog => {
          const row: Record<string, any> = {
            'Struttura':              risorsa.struttura,
            'Cognome':                risorsa.cognome,
            'Nome':                   risorsa.nome,
            'ID HRP':                 prog.id_hrp ?? '',
            'Titolo Progetto':        prog.progetto_titolo,
            'PM':                     prog.pm,
            'Cliente':                prog.cliente,
            'Budget (gg/uu)':         prog.budget_gg_uu || 0,
            'Budget Residuo (gg/uu)': prog.budget_residuo_gg_uu || 0,
          };
          mesi.forEach(m => { row[MESI_LABELS[m]] = prog.mesi[m] || 0; });
          worksheet.addRow(row);
        });
      });

      // Stile intestazione
      const headerRow = worksheet.getRow(1);
      headerRow.height = 22;
      headerRow.eachCell(cell => {
        cell.font      = { bold: true, color: { argb: HEADER_FG }, size: 11 };
        cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border    = thinBorder;
      });

      // Stile righe dati
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        const bgColor = rowNumber % 2 === 0 ? ROW_EVEN_BG : ROW_ODD_BG;
        row.height = 18;
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
          cell.border    = thinBorder;
          cell.alignment = { vertical: 'middle' };
          const col = worksheet.getColumn(colNumber);
          if (col && typeof cell.value === 'number') {
            cell.alignment = { vertical: 'middle', horizontal: 'right' };
          }
        });
      });

      worksheet.views = [{ state: 'frozen', ySplit: 1, xSplit: 0 }];
      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to:   { row: 1, column: worksheet.columns.length },
      };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob   = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url    = URL.createObjectURL(blob);
      const link   = document.createElement('a');
      const strutturaSuffix = strutturaFilter ? `_${strutturaFilter.replace(/\s+/g, '_')}` : '';
      link.href     = url;
      link.download = `Pianificazione_${anno}${strutturaSuffix}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError('Errore durante l\'esportazione: ' + e.message);
    } finally {
      setExporting(false);
    }
  }, [visibleGroups, mesi, anno, strutturaFilter]);

  const totalCols = 11 + mesi.length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2 }}>

        {/* Toolbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Vista Pianificazione {anno}
          </Typography>

          {/* Filtro anno */}
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <InputLabel>Anno</InputLabel>
            <Select
              value={anno}
              label="Anno"
              onChange={e => setAnno(Number(e.target.value))}
            >
              {ANNO_OPTIONS.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Filtro struttura */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Struttura</InputLabel>
            <Select
              value={strutturaFilter}
              label="Struttura"
              onChange={e => setStrutturaFilter(e.target.value)}
            >
              <MenuItem value="">Tutte le strutture</MenuItem>
              {strutture.map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Espandi / Collassa */}
          <ButtonGroup size="small" variant="outlined">
            <Button startIcon={<UnfoldMore />} onClick={expandAll}>Espandi</Button>
            <Button startIcon={<UnfoldLess />} onClick={collapseAll}>Collassa</Button>
          </ButtonGroup>

          {/* Esporta Excel */}
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownload />}
            onClick={handleExport}
            disabled={exporting || loading || visibleGroups.length === 0}
          >
            {exporting ? 'Export...' : 'Esporta Excel'}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 'calc(100vh - 220px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...thSx, width: 32, minWidth: 32, padding: '6px 4px' }} />
                  <TableCell sx={{ ...thSx, minWidth: 120 }}>Struttura</TableCell>
                  <TableCell sx={{ ...thSx, minWidth: 110 }}>Cognome</TableCell>
                  <TableCell sx={{ ...thSx, minWidth: 90 }}>Nome</TableCell>
                  <TableCell sx={{ ...thSx, minWidth: 70 }}>ID HRP</TableCell>
                  <TableCell sx={{ ...thSx, minWidth: 200 }}>Titolo Progetto</TableCell>
                  <TableCell sx={{ ...thSx, minWidth: 120 }}>PM</TableCell>
                  <TableCell sx={{ ...thSx, minWidth: 120 }}>Cliente</TableCell>
                  <TableCell sx={{ ...thSx, minWidth: 90, textAlign: 'right' }}>Budget (gg)</TableCell>
                  <TableCell sx={{ ...thSx, minWidth: 90, textAlign: 'right' }}>Residuo (gg)</TableCell>
                  <TableCell sx={thPercAllSx}>% All</TableCell>
                  {mesi.map(m => (
                    <TableCell key={m} sx={thMeseSx}>{MESI_LABELS[m]}</TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleGroups.map(risorsa => {
                  const isOpen = expanded.has(risorsa.key);
                  const pAllStyle = percAllStyle(risorsa.percAll);

                  return (
                    <React.Fragment key={risorsa.key}>
                      <TableRow
                        hover
                        onClick={() => toggleExpand(risorsa.key)}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: '#e3eaf6',
                          '&:hover': { backgroundColor: '#d0ddf0' },
                        }}
                      >
                        <TableCell sx={{ padding: '2px 4px', borderRight: '1px solid #ccc' }}>
                          <IconButton size="small" sx={{ p: 0 }}>
                            {isOpen
                              ? <KeyboardArrowDown fontSize="small" />
                              : <KeyboardArrowRight fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{risorsa.struttura}</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{risorsa.cognome}</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{risorsa.nome}</TableCell>
                        <TableCell /><TableCell /><TableCell /><TableCell />
                        <TableCell /><TableCell />
                        <TableCell align="center">
                          <Tooltip title={`Media mensile: ${risorsa.percAll}%`}>
                            <Box sx={{
                              ...pAllStyle,
                              borderRadius: 1, px: 0.5, py: 0.25,
                              display: 'inline-block', fontSize: '0.75rem',
                              minWidth: 36, textAlign: 'center',
                            }}>
                              {risorsa.percAll}%
                            </Box>
                          </Tooltip>
                        </TableCell>
                        {mesi.map(m => {
                          const val = risorsa.mesiTotali[m] || 0;
                          return (
                            <TableCell key={m} align="center" sx={{ fontSize: '0.75rem', fontWeight: 600, ...(val > 0 ? percAllStyle(val) : {}) }}>
                              {val || ''}
                            </TableCell>
                          );
                        })}
                      </TableRow>

                      {isOpen && risorsa.progetti.map((prog, idx) => {
                        const pStyle = percAllStyle(prog.percAll);
                        return (
                          <TableRow
                            key={`${risorsa.key}|${prog.progetto_titolo}|${idx}`}
                            sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}
                          >
                            <TableCell />
                            <TableCell sx={{ fontSize: '0.75rem', color: '#666' }}>{risorsa.struttura}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: '#666' }}>{risorsa.cognome}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: '#666' }}>{risorsa.nome}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{prog.id_hrp ?? ''}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{prog.progetto_titolo}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{prog.pm}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{prog.cliente}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                              {prog.budget_gg_uu ? prog.budget_gg_uu.toFixed(1) : ''}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                              {prog.budget_residuo_gg_uu ? prog.budget_residuo_gg_uu.toFixed(1) : ''}
                            </TableCell>
                            <TableCell align="center">
                              {prog.percAll > 0 && (
                                <Box sx={{
                                  ...pStyle,
                                  borderRadius: 1, px: 0.5, py: 0.25,
                                  display: 'inline-block', fontSize: '0.7rem',
                                  minWidth: 36, textAlign: 'center',
                                }}>
                                  {prog.percAll}%
                                </Box>
                              )}
                            </TableCell>
                            {mesi.map(m => {
                              const val = prog.mesi[m] || 0;
                              return (
                                <TableCell key={m} align="center" sx={{ fontSize: '0.75rem', ...(val > 0 ? percAllStyle(val) : {}) }}>
                                  {val || ''}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}

                {visibleGroups.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={totalCols} align="center" sx={{ py: 4, color: '#999' }}>
                      Nessuna pianificazione trovata per l'anno {anno}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default PianificazioneVistaPage;
